import {
  type PurchaseRequestSummary,
  RFQ_INVITE_STATUS_LABEL,
  RFQ_OFFER_FILE_STATUSES,
  RFQ_STATUSES,
  type RfqDetail,
  type RfqInviteStatus,
  type RfqStatus,
  type RfqSummary,
  seesMoney,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { FileLink } from '../../components/FileLink';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, dateTime, tenge } from '../../lib/format';
import { useCan, useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel } from '../../ui/layout';

const TONES: Record<RfqStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  draft: 'neutral',
  sent: 'info',
  collecting: 'warning',
  compared: 'success',
  closed: 'neutral',
  cancelled: 'danger',
};

const INVITE_TONES: Record<RfqInviteStatus, 'neutral' | 'info' | 'warning' | 'success' | 'danger'> = {
  draft: 'neutral',
  sent: 'neutral',
  opened: 'info',
  downloaded: 'info',
  will_participate: 'warning',
  declined: 'danger',
  submitted: 'success',
};

/** Поставщик ещё в работе: не подал КП и не отказался */
const inProgress = (s: RfqInviteStatus) => s !== 'submitted' && s !== 'declined' && s !== 'draft';

const rfqsQuery = {
  queryKey: ['procurement', 'rfqs'],
  queryFn: () => api<RfqSummary[]>('/procurement/rfqs'),
};
const rfqQuery = (id: string) => ({
  queryKey: ['procurement', 'rfqs', id],
  queryFn: () => api<RfqDetail>(`/procurement/rfqs/${id}`),
});

/** Новый запрос КП из заявок, которые уже на этапе «Сбор КП» (или на проверке) */
function NewRfqDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const reqs = useQuery({
    queryKey: ['procurement', 'requests', 'for-rfq'],
    queryFn: () => api<PurchaseRequestSummary[]>('/procurement/requests', { query: { stage: 'rfq' } }),
    enabled: open,
  });
  const [picked, setPicked] = useState<string[]>([]);
  const [categories, setCategories] = useState('');
  const [responseDue, setResponseDue] = useState('');
  const [address, setAddress] = useState('');
  const [requirements, setRequirements] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api<RfqDetail>('/procurement/rfqs', {
        body: {
          requestIds: picked,
          responseDue: responseDue || null,
          deliveryAddress: address || null,
          requirements: requirements || null,
          categories: categories
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean),
        },
      }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['procurement', 'rfqs'] });
      toast(`Запрос ${r.number} создан`);
      onOpenChange(false);
      setPicked([]);
    },
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Новый запрос КП"
      description="Одна или несколько заявок на этапе «Сбор КП»: позиции берутся из них"
      footer={
        <Button
          variant="primary"
          disabled={!picked.length}
          loading={create.isPending}
          onClick={() => create.mutate()}
        >
          Создать запрос
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        {reqs.isPending ? <Spinner /> : null}
        {reqs.data && !reqs.data.length ? (
          <p className="text-[14px] text-ink-2">
            Нет заявок на этапе «Сбор КП». Переведите заявку на этот этап.
          </p>
        ) : null}
        <div className="flex flex-col gap-1">
          {reqs.data?.map((r) => (
            <label key={r.id} className="flex min-h-11 items-center gap-3 text-[14px]">
              <input
                type="checkbox"
                className="size-5"
                checked={picked.includes(r.id)}
                onChange={(e) =>
                  setPicked(e.target.checked ? [...picked, r.id] : picked.filter((x) => x !== r.id))
                }
              />
              <span>
                {r.number} {r.title} <span className="text-ink-2">· {r.projectCode}</span>
              </span>
            </label>
          ))}
        </div>
        <Field label="Срок ответа поставщиков">
          <Input type="date" value={responseDue} onChange={(e) => setResponseDue(e.target.value)} />
        </Field>
        <Field label="Адрес доставки">
          <Input value={address} onChange={(e) => setAddress(e.target.value)} />
        </Field>
        <Field
          label="Категории поставщиков"
          hint="Через запятую: по ним подбираются поставщики из реестра контрагентов"
        >
          <Input value={categories} onChange={(e) => setCategories(e.target.value)} />
        </Field>
        <Field label="Требования">
          <Textarea value={requirements} onChange={(e) => setRequirements(e.target.value)} rows={3} />
        </Field>
        {create.error ? <ErrorBox error={create.error} /> : null}
      </div>
    </Dialog>
  );
}

/** Вкладка «Запросы КП» в закупках */
export function RfqsTab() {
  const q = useQuery(rfqsQuery);
  const canManage = useCan('procurement.rfq.manage');
  const [open, setOpen] = useState(false);
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  return (
    <div className="flex flex-col gap-3">
      {canManage ? (
        <div>
          <Button variant="primary" icon={<Plus className="size-4" />} onClick={() => setOpen(true)}>
            Новый запрос КП
          </Button>
        </div>
      ) : null}
      {!q.data.length ? (
        <EmptyState title="Запросов КП пока нет">Запрос собирается из заявок на этапе «Сбор КП»</EmptyState>
      ) : (
        <Panel>
          <ul className="divide-y divide-line">
            {q.data.map((r) => (
              <li key={r.id}>
                <Link
                  to={`/procurement/rfqs/${r.id}`}
                  className="flex min-h-11 flex-wrap items-center justify-between gap-2 px-5 py-3 hover:bg-surface-2"
                >
                  <span className="font-medium text-ink">
                    {r.number} {r.title}
                  </span>
                  <span className="flex items-center gap-3 text-[13px] text-ink-2">
                    заявок {r.requests} · поставщиков {r.responded}/{r.invited}
                    {r.responseDue ? ` · ответ до ${date(r.responseDue)}` : ''}
                    <Badge tone={r.overdue ? 'danger' : TONES[r.status]}>
                      {r.overdue ? 'Срок ответа прошёл' : RFQ_STATUSES[r.status]}
                    </Badge>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}
      <NewRfqDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

const ACTIONS: Partial<Record<RfqStatus, string>> = {
  sent: 'Разослать поставщикам',
  compared: 'Сравнение готово',
  collecting: 'Продолжить приём ответов',
  closed: 'Закрыть запрос',
};

export function RfqPage() {
  const { rfqId } = useParams({ strict: false }) as { rfqId: string };
  const qc = useQueryClient();
  const me = useMe();
  const canManage = useCan('procurement.rfq.manage');
  const showMoney = seesMoney(me.access, 'acts_invoices');
  const q = useQuery(rfqQuery(rfqId));
  useCrumbs(
    q.data
      ? [
          { label: 'Запросы КП', to: '/procurement?tab=rfqs' },
          { label: q.data.number, title: q.data.title },
        ]
      : null,
    q.error,
  );
  const refresh = (r: RfqDetail) => qc.setQueryData(['procurement', 'rfqs', rfqId], r);
  const done = (r: RfqDetail) => {
    refresh(r);
    void qc.invalidateQueries({ queryKey: ['procurement', 'rfqs'] });
  };
  const move = useMutation({
    mutationFn: (v: { to: RfqStatus; reason?: string }) =>
      api<RfqDetail>(`/procurement/rfqs/${rfqId}/transition`, { body: v }),
    onSuccess: done,
  });
  const invite = useMutation({
    mutationFn: (byCategories: boolean) =>
      api<RfqDetail>(`/procurement/rfqs/${rfqId}/invites`, { body: { byCategories } }),
    onSuccess: (r) => {
      done(r);
      toast(`Приглашено поставщиков: ${r.invited}`);
    },
  });
  const step = useMutation({
    mutationFn: (v: { supplierId: string; step: 'opened' | 'downloaded' | 'will_participate' }) =>
      api<RfqDetail>(`/procurement/rfqs/${rfqId}/invites/${v.supplierId}/step`, { body: { step: v.step } }),
    onSuccess: done,
  });
  const decline = useMutation({
    mutationFn: (v: { supplierId: string; reason: string }) =>
      api<RfqDetail>(`/procurement/rfqs/${rfqId}/decline`, { body: v }),
    onSuccess: done,
  });
  const msg = useMutation({
    mutationFn: (body: string) =>
      api<RfqDetail>(`/procurement/rfqs/${rfqId}/messages`, { body: { kind: 'note', body } }),
    onSuccess: done,
  });
  const confirmFile = useMutation({
    mutationFn: (fileId: string) =>
      api<RfqDetail>(`/procurement/rfqs/${rfqId}/offer-files/${fileId}/confirm`, { body: {} }),
    onSuccess: (r) => {
      done(r);
      toast('Позиции из файла подтверждены');
    },
  });
  const [text, setText] = useState('');
  const [declining, setDeclining] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const r = q.data;
  return (
    <Page>
      <PageHeader
        title={`${r.number} ${r.title}`}
        subtitle={`${RFQ_STATUSES[r.status]}${r.responseDue ? ` · ответ до ${date(r.responseDue)}` : ''}`}
        actions={
          canManage ? (
            <div className="flex flex-wrap gap-2">
              {r.nextStatuses
                .filter((s) => ACTIONS[s] && !(s === 'collecting' && r.status === 'sent'))
                .map((s) => (
                  <Button
                    key={s}
                    variant="primary"
                    loading={move.isPending}
                    onClick={() => move.mutate({ to: s })}
                  >
                    {ACTIONS[s]}
                  </Button>
                ))}
              {r.nextStatuses.includes('cancelled') ? (
                <Button
                  onClick={() => {
                    const reason = window.prompt('Причина отмены запроса');
                    if (reason) move.mutate({ to: 'cancelled', reason });
                  }}
                >
                  Отменить
                </Button>
              ) : null}
            </div>
          ) : null
        }
      />
      {msg.error ? <ErrorBox error={msg.error} className="mb-3" /> : null}
      {move.error ? <ErrorBox error={move.error} className="mb-3" /> : null}
      <div className="grid gap-4 md:grid-cols-2">
        <Panel title="Заявки и позиции">
          <ul className="divide-y divide-line text-[14px]">
            {r.requestList.map((x) => (
              <li key={x.id} className="px-5 py-2.5">
                <Link to={`/procurement/${x.id}`} className="font-medium hover:underline">
                  {x.number} {x.title}
                </Link>{' '}
                <span className="text-ink-2">· {x.projectCode}</span>
              </li>
            ))}
          </ul>
          <ul className="border-t border-line px-5 py-3 text-[14px] text-ink-2">
            {r.lineList.map((l) => (
              <li key={l.id}>
                {l.name} — {l.quantity} {l.unit}
              </li>
            ))}
          </ul>
          {r.deliveryAddress ? <p className="px-5 pb-3 text-[14px]">Доставка: {r.deliveryAddress}</p> : null}
          {r.requirements ? <p className="px-5 pb-3 text-[14px]">Требования: {r.requirements}</p> : null}
        </Panel>
        <Panel
          title="Поставщики"
          meta={`ответили ${r.responded} из ${r.invited}`}
          actions={
            canManage && ['draft', 'sent', 'collecting'].includes(r.status) ? (
              <Button loading={invite.isPending} onClick={() => invite.mutate(true)}>
                Подобрать по категориям
              </Button>
            ) : null
          }
        >
          {(invite.error ?? step.error ?? decline.error) ? (
            <ErrorBox error={(invite.error ?? step.error ?? decline.error) as Error} className="m-3" />
          ) : null}
          {declining ? (
            <form
              className="flex flex-col gap-2 border-b border-line p-4 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                if (reason.trim().length >= 3) step.reset();
                decline.mutate(
                  { supplierId: declining, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setDeclining(null);
                      setReason('');
                    },
                  },
                );
              }}
            >
              <Input
                aria-label="Причина отказа поставщика"
                placeholder="Причина отказа поставщика"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <Button type="submit" loading={decline.isPending}>
                Записать отказ
              </Button>
              <Button variant="secondary" onClick={() => setDeclining(null)}>
                Отмена
              </Button>
            </form>
          ) : null}
          {!r.inviteList.length ? (
            <p className="px-5 py-4 text-[14px] text-ink-2">
              Поставщиков нет.{' '}
              {r.categories.length
                ? ''
                : 'Укажите категории запроса — по ним подбираются поставщики из реестра контрагентов.'}
            </p>
          ) : (
            <ul className="divide-y divide-line text-[14px]">
              {r.inviteList.map((i) => (
                <li
                  key={i.supplierId}
                  className="flex flex-wrap items-center justify-between gap-2 px-5 py-2.5"
                >
                  <Link to={`/suppliers/${i.supplierId}`} className="font-medium hover:underline">
                    {i.supplierName}
                  </Link>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge tone={INVITE_TONES[i.status]}>
                      {RFQ_INVITE_STATUS_LABEL[i.status]}
                      {i.status === 'submitted' && i.respondedAt ? ` ${dateTime(i.respondedAt)}` : ''}
                    </Badge>
                    {i.status === 'declined' && i.declineReason ? (
                      <span className="text-[13px] text-ink-2">{i.declineReason}</span>
                    ) : null}
                    {canManage &&
                    ['sent', 'collecting'].includes(r.status) &&
                    i.sentAt &&
                    inProgress(i.status) ? (
                      <>
                        {i.status === 'sent' ? (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              decline.reset();
                              step.mutate({ supplierId: i.supplierId, step: 'opened' });
                            }}
                          >
                            Открыл
                          </Button>
                        ) : null}
                        {['sent', 'opened'].includes(i.status) ? (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              decline.reset();
                              step.mutate({ supplierId: i.supplierId, step: 'downloaded' });
                            }}
                          >
                            Скачал документы
                          </Button>
                        ) : null}
                        {i.status !== 'will_participate' ? (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              step.mutate({ supplierId: i.supplierId, step: 'will_participate' })
                            }
                          >
                            Будет участвовать
                          </Button>
                        ) : null}
                        <Button variant="secondary" onClick={() => setDeclining(i.supplierId)}>
                          Отказался
                        </Button>
                      </>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {r.offerList.length ? (
            <p className="border-t border-line px-5 py-3 text-[13px] text-ink-2">
              {showMoney
                ? 'Цены ответов — в заявках, на вкладке «КП и сравнение».'
                : 'Цены ответов видят сотрудники с уровнем денег «Акты и счета» и выше.'}
            </p>
          ) : null}
        </Panel>
      </div>
      {r.offerFiles?.length ? (
        <Panel title="Файлы ответов поставщиков" className="mt-4" meta="ЗК-19">
          {confirmFile.error ? <ErrorBox error={confirmFile.error} className="m-3" /> : null}
          <ul className="divide-y divide-line text-[14px]">
            {r.offerFiles.map((f) => (
              <li key={f.id} className="flex flex-col gap-2 px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-ink">{f.supplierName}</div>
                    <FileLink file={{ id: f.fileId, name: f.fileName, scanState: 'clean' }} />
                    {f.parseNote ? <p className="mt-1 text-[13px] text-ink-2">{f.parseNote}</p> : null}
                  </div>
                  <Badge
                    tone={
                      f.parseStatus === 'confirmed'
                        ? 'success'
                        : f.parseStatus === 'failed'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {RFQ_OFFER_FILE_STATUSES[f.parseStatus]}
                  </Badge>
                </div>
                {showMoney && f.draftPrices && Object.keys(f.draftPrices).length ? (
                  <ul className="rounded-md bg-tile/50 px-3 py-2 text-[13px] text-ink-2">
                    {r.lineList
                      .filter((l) => f.draftPrices?.[l.id] != null)
                      .map((l) => (
                        <li key={l.id}>
                          {l.name}: {tenge(f.draftPrices![l.id]!)}
                        </li>
                      ))}
                  </ul>
                ) : null}
                {canManage &&
                (f.parseStatus === 'parsed_draft' || f.parseStatus === 'pending_review') &&
                !['closed', 'cancelled'].includes(r.status) ? (
                  <div className="flex flex-col gap-2">
                    {f.parseStatus === 'pending_review' ? (
                      <p className="text-[13px] text-ink-2">
                        Файл на проверке без авторазбора: сверьте скан, при необходимости внесите КП в заявке
                        {r.requestList[0] ? (
                          <>
                            {' '}
                            <Link to={`/procurement/${r.requestList[0].id}`} className="underline">
                              {r.requestList[0].number}
                            </Link>
                          </>
                        ) : null}
                        , затем подтвердите.
                      </p>
                    ) : null}
                    <Button
                      className="min-h-11 w-full sm:w-auto"
                      loading={confirmFile.isPending}
                      onClick={() => confirmFile.mutate(f.id)}
                    >
                      {f.parseStatus === 'parsed_draft' ? 'Подтвердить позиции' : 'Отметить проверенным'}
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel title="Вопросы и ответы" className="mt-4">
        <ul className="divide-y divide-line text-[14px]">
          {r.messages.map((m) => (
            <li key={m.id} className="px-5 py-2.5">
              <div className="text-[12px] text-ink-2">
                {m.supplierName ?? 'Всем'} · {dateTime(m.at)}
              </div>
              {m.body}
            </li>
          ))}
        </ul>
        {canManage && !['closed', 'cancelled'].includes(r.status) ? (
          <form
            className="flex flex-col gap-2 border-t border-line p-4 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) msg.mutate(text.trim(), { onSuccess: () => setText('') });
            }}
          >
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Сообщение всем приглашённым"
              aria-label="Сообщение"
            />
            <Button type="submit" loading={msg.isPending}>
              Отправить
            </Button>
          </form>
        ) : null}
      </Panel>
    </Page>
  );
}
